import { EventName, EventType, metrics } from '@prophecy/metrics';

import { logMark, logTerminalReport, registerJourney } from './debugConsole';

import type {
  AssetBucket,
  AssetDelivery,
  AssetEntry,
  AssetSummary,
  CallSummaryEntry,
  EmitResult,
  JourneyConfig,
  JourneyState,
  RecordCallOpts,
  TimedCall
} from './types';
import type { TrackArgs } from '@prophecy/metrics';

// The browser's default Resource Timing buffer is small enough that a long SQL IDE session (heavy
// GraphQL/REST polling + WS + chunks, on top of the initial asset burst) fills it, after which the
// browser silently stops recording ANY new entries — not a rolling FIFO, a hard stop. Early
// page-load assets stay visible (they got in before the cap); every fetch after that point gets no
// entry at all, so `matchResourceTiming` finds nothing to correlate against for the rest of the session.
// Grow it once, generously, at module load.
if (typeof performance !== 'undefined' && performance.setResourceTimingBufferSize) {
  performance.setResourceTimingBufferSize(5000);
}

const SLOWEST_ASSETS_LIMIT = 8;

export type JourneyDeltas = Record<string, number | null>;

/** Aggregates timed calls by name+kind per journey. */
export function summarizeCalls(calls: TimedCall[]): CallSummaryEntry[] {
  const map = new Map<string, CallSummaryEntry>();
  for (const call of calls) {
    const key = `${call.kind}:${call.name}`;
    const prev = map.get(key) ?? {
      name: call.name,
      kind: call.kind,
      count: 0,
      totalMs: 0,
      maxMs: 0,
      earliestStartedAt: call.startedAt,
      latestEndedAt: call.endedAt
    };
    prev.count += 1;
    prev.totalMs += call.durationMs;
    prev.maxMs = Math.max(prev.maxMs, call.durationMs);
    prev.earliestStartedAt = Math.min(prev.earliestStartedAt, call.startedAt);
    prev.latestEndedAt = Math.max(prev.latestEndedAt, call.endedAt);
    if (call.serverMs != null) {
      prev.totalServerMs = (prev.totalServerMs ?? 0) + call.serverMs;
      prev.maxServerMs = Math.max(prev.maxServerMs ?? 0, call.serverMs);
    }
    if (call.networkMs != null) {
      prev.totalNetworkMs = (prev.totalNetworkMs ?? 0) + call.networkMs;
      prev.maxNetworkMs = Math.max(prev.maxNetworkMs ?? 0, call.networkMs);
    }
    if (call.resourceDurationMs != null) {
      prev.totalResourceDurationMs = (prev.totalResourceDurationMs ?? 0) + call.resourceDurationMs;
      prev.maxResourceDurationMs = Math.max(prev.maxResourceDurationMs ?? 0, call.resourceDurationMs);
    }
    map.set(key, prev);
  }
  return [...map.values()].sort((a, b) => b.totalMs - a.totalMs || b.maxMs - a.maxMs);
}

export function shortenResourceName(url: string): string {
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined);
    const path = u.pathname.split('/').filter(Boolean);
    return path.slice(-2).join('/') || u.pathname;
  } catch {
    return url.slice(-80);
  }
}

/**
 * `responseEnd - requestStart` for one Resource Timing entry — real network activity once the
 * browser dispatched the request (TTFB + body download), excluding only the pre-dispatch
 * queueing/scheduling delay (which `duration`, measured from `startTime`, does include). `undefined`,
 * not a fake `0`, when the entry is cross-origin without a `Timing-Allow-Origin` response header: the
 * spec zeroes `requestStart` in that case (privacy), and a real request made well after navigation
 * start can never legitimately have `requestStart === 0`.
 */
function networkMs(entry: PerformanceResourceTiming): number | undefined {
  if (!entry.requestStart) return undefined;
  return Math.round(entry.responseEnd - entry.requestStart);
}

/**
 * `fromMs` should almost always be `0` (document time origin), not a journey's `startedAt` — on an
 * SPA nav, `startedAt` is the click time, well after chunks idle-prefetched during the prior page
 * already finished loading. Filtering by a later `fromMs` excludes every one of them and every
 * bucket reports empty.
 *
 * Delivery classification, most reliable signal first:
 * - `deliveryType === 'cache'` (Chrome 121+) or transferSize === 0 with a body -> memory/disk cache.
 * - `responseStatus === 304` (Chrome 109+), or header-sized transfer with a much larger body ->
 *   revalidated: the body came from cache but a conditional round-trip was still paid.
 * - everything else -> full network download.
 */
export function collectAssetSummary(fromMs: number, toMs: number): AssetSummary {
  const empty = (): AssetBucket => ({
    count: 0,
    totalMs: 0,
    transferBytes: 0,
    earliestStartedAt: 0,
    latestEndedAt: 0
  });
  const summary: AssetSummary = { cached: empty(), revalidated: empty(), network: empty(), slowest: [] };

  if (typeof performance === 'undefined' || !performance.getEntriesByType) {
    return summary;
  }
  const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  const candidates: AssetEntry[] = [];

  for (const entry of entries) {
    const initiator = entry.initiatorType;
    if (
      initiator !== 'script' &&
      initiator !== 'link' &&
      initiator !== 'css' &&
      initiator !== 'other' &&
      initiator !== ''
    ) {
      continue;
    }
    if (entry.responseEnd > toMs || entry.responseEnd < fromMs) {
      continue;
    }

    const transferBytes = entry.transferSize ?? 0;
    const decoded = entry.decodedBodySize ?? 0;
    const encoded = entry.encodedBodySize ?? 0;
    // Not yet in the lib.dom PerformanceResourceTiming type.
    const { deliveryType, responseStatus } = entry as { deliveryType?: string; responseStatus?: number };
    const body = Math.max(decoded, encoded);

    let bucket: AssetDelivery;
    if (deliveryType === 'cache' || (transferBytes === 0 && body > 0)) {
      bucket = 'cached';
    } else if (responseStatus === 304 || (transferBytes > 0 && encoded > 0 && transferBytes < encoded)) {
      bucket = 'revalidated';
    } else {
      bucket = 'network';
    }

    const durationMs = Math.round(entry.duration);
    if (bucket === 'network' && transferBytes === 0 && durationMs === 0) {
      continue;
    }

    const startedAt = Math.round(entry.startTime);
    const endedAt = Math.round(entry.responseEnd);
    if (summary[bucket].count === 0) {
      summary[bucket].earliestStartedAt = startedAt;
      summary[bucket].latestEndedAt = endedAt;
    } else {
      summary[bucket].earliestStartedAt = Math.min(summary[bucket].earliestStartedAt, startedAt);
      summary[bucket].latestEndedAt = Math.max(summary[bucket].latestEndedAt, endedAt);
    }
    summary[bucket].count += 1;
    summary[bucket].totalMs += durationMs;
    summary[bucket].transferBytes += transferBytes;
    const dnsMs = Math.round((entry.domainLookupEnd ?? 0) - (entry.domainLookupStart ?? 0));
    const entryNetworkMs = networkMs(entry);
    if (entryNetworkMs != null) {
      summary[bucket].totalNetworkMs = (summary[bucket].totalNetworkMs ?? 0) + entryNetworkMs;
    }
    candidates.push({
      name: shortenResourceName(entry.name),
      durationMs,
      delivery: bucket,
      transferBytes,
      dnsMs,
      networkMs: entryNetworkMs,
      startedAt,
      endedAt
    });
  }

  summary.cached.totalMs = Math.round(summary.cached.totalMs);
  summary.revalidated.totalMs = Math.round(summary.revalidated.totalMs);
  summary.network.totalMs = Math.round(summary.network.totalMs);
  summary.slowest = candidates.sort((a, b) => b.durationMs - a.durationMs).slice(0, SLOWEST_ASSETS_LIMIT);
  return summary;
}

const TIMING_MATCH_SLOP_MS = 5;

/** Resource Timing entries already claimed by an earlier `matchResourceTiming` call, so two
 * concurrent calls to the same URL can't both attribute themselves to the same network entry. */
const consumedTimingEntries = new WeakSet<PerformanceResourceTiming>();

/** Caps `JourneyState.calls` for a journey that stalls before its terminal mark ever fires. */
const MAX_TIMED_CALLS_PER_JOURNEY = 1000;

export type ResourceTimingMatch = {
  /** `responseEnd - requestStart` — TTFB + body download. See `TimedCall.networkMs`. */
  networkMs: number;
  /** The matched entry's own `duration` (`responseEnd - startTime`). See `TimedCall.resourceDurationMs`. */
  resourceDurationMs: number;
};

/**
 * Best-effort network-attributed timing for a GraphQL or REST round-trip, read off the Resource
 * Timing entry the browser already recorded for that fetch. `networkMs` (TTFB + body download) is
 * not pure server time; `resourceDurationMs` is the same entry's own `duration`, which additionally
 * includes the pre-dispatch queueing/scheduling delay `networkMs` excludes. Both distinct from the
 * caller's JS-observed wall duration, which also includes request setup and response JSON parsing.
 * For WS, prefer the frame's `timeTakenInMillis` instead (a real backend value, exposed as
 * `serverMs`) — there is no per-message Resource Timing entry.
 *
 * Correlates by URL + time window, not by identity — there is no per-call marker the backend echoes
 * back. For most REST calls the URL alone disambiguates (each endpoint is distinct), but every
 * GraphQL operation goes through the same one or two fixed endpoint URLs (see `data/util.ts`), so for
 * those this can only narrow by `[windowStart, windowEnd]` (with a small slop) and pick the most
 * recently completed matching entry that no earlier call has already claimed. Under concurrent calls
 * to the same URL that complete out of order, this can misattribute — there is no exact fix without a
 * per-call marker the backend echoes back.
 */
export function matchResourceTiming(
  url: string,
  windowStart: number,
  windowEnd: number
): ResourceTimingMatch | undefined {
  if (typeof performance === 'undefined' || !performance.getEntriesByType) return undefined;
  const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];

  let best: PerformanceResourceTiming | undefined;
  for (const entry of entries) {
    if (entry.name !== url) continue;
    if (consumedTimingEntries.has(entry)) continue;
    if (entry.startTime < windowStart - TIMING_MATCH_SLOP_MS) continue;
    if (entry.responseEnd > windowEnd + TIMING_MATCH_SLOP_MS) continue;
    if (!best || entry.responseEnd > best.responseEnd) best = entry;
  }
  if (!best) return undefined;

  // Only claimed once it's actually usable — a cross-origin entry with no Timing-Allow-Origin
  // header (see networkMs) would otherwise burn this entry for every later, possibly-matching call.
  const matchedNetworkMs = networkMs(best);
  if (matchedNetworkMs == null) return undefined;
  consumedTimingEntries.add(best);
  return { networkMs: matchedNetworkMs, resourceDurationMs: Math.round(best.duration) };
}

/**
 * Cross-journey global: the shared GraphQL/REST wrappers (`data/util.ts`, `data/rest/makeRestCall.ts`)
 * have no journey id in scope, so they attribute a call to whichever journey is "current" instead.
 * Valid only when at most one journey is genuinely in flight at a time — see PERF_FRAMEWORK.md for
 * the constraint this rests on.
 */
let current: { registry: JourneyRegistry<string>; id: string } | null = null;

export function recordCurrentJourneyCall(name: string, durationMs: number, opts: RecordCallOpts): void {
  current?.registry.recordCall(current.id, name, durationMs, opts);
}

/**
 * `EmitResult['payload']` is `Record<string, unknown>` — nothing here (or in a config's `emit`
 * callback) checks a payload's keys against its `eventName`'s actual schema, so a typo'd key is
 * only ever caught by manually cross-checking `packages/metrics/src/generated/schema.ts`.
 */
function emitAll(result: EmitResult | EmitResult[] | undefined): void {
  if (!result) return;
  for (const event of Array.isArray(result) ? result : [result]) {
    if (!event) continue;
    metrics.track(event as unknown as TrackArgs);
  }
}

export class JourneyRegistry<Mark extends string> {
  private active = new Map<string, JourneyState<Mark>>();

  private pending = new Map<string, { mark: Mark; at: number }[]>();

  constructor(private config: JourneyConfig<Mark>) {
    registerJourney(config.name, this);
  }

  // Both default to on — every integration gets call summaries and asset timing for free without
  // remembering to opt in. Pass `false` explicitly to opt out (no journey currently does).
  private get timedCallsEnabled(): boolean {
    return this.config.timedCalls !== false;
  }

  private get assetLoadTimeEnabled(): boolean {
    return this.config.assetLoadTime !== false;
  }

  markPending(mark: Mark, id: string): void {
    const at = performance.now();
    const list = this.pending.get(id) ?? [];
    list.push({ mark, at });
    this.pending.set(id, list);
  }

  begin(
    id: string,
    opts?: {
      startedAt?: number;
      mode?: 'reload' | 'spa';
      seedMarks?: Partial<Record<Mark, number>>;
      seedCalls?: TimedCall[];
    }
  ): void {
    const pendingMarks = this.pending.get(id);
    // Default startedAt: the earliest buffered pre-begin signal (e.g. a click before this page
    // mounted) if one exists, else 0 — `performance.now()` is already relative to this document's
    // own navigation start, so 0 means "from when this document began loading" for a hard
    // reload/deep link, without the caller needing to know which case it's in.
    const earliestPending = pendingMarks?.length ? Math.min(...pendingMarks.map((p) => p.at)) : undefined;
    const startedAt = opts?.startedAt ?? earliestPending ?? 0;
    // NOT "any pending mark exists": a journey can buffer other marks before begin() that fire on
    // every mount regardless of navigation origin (e.g. a websocket connect-start timestamp,
    // buffered during render ahead of the mount effect that calls begin()) — those must not read as
    // an SPA signal just because they happened to arrive early. Only the mark the config designates
    // as the real navigation signal counts. `opts.mode` overrides this entirely — see
    // `JourneyHandle.begin`'s doc comment for why (resuming a cross-reload snapshot).
    const hasNavigationSignal =
      this.config.navigationMark != null && pendingMarks?.some((p) => p.mark === this.config.navigationMark);
    const mode: 'reload' | 'spa' = opts?.mode ?? (opts?.startedAt || hasNavigationSignal ? 'spa' : 'reload');
    const state: JourneyState<Mark> = {
      id,
      startedAt,
      mode,
      // Silent seed, not a real mark() call per entry — no emit/logMark re-fires for marks that
      // already happened for real on a prior document (see `getSnapshot`/resuming across a reload).
      marks: opts?.seedMarks ? { ...opts.seedMarks } : {},
      meta: {},
      calls: this.timedCallsEnabled ? [...(opts?.seedCalls ?? [])] : undefined
    };

    if (pendingMarks) {
      for (const { mark, at } of pendingMarks) {
        state.marks[mark] = at;
      }
      this.pending.delete(id);
    }

    this.active.set(id, state);
    // Safe despite the generic mismatch: `current` only ever calls `recordCall`, whose signature
    // doesn't reference `Mark` — the structural check below is stricter than the actual usage.
    current = { registry: this as unknown as JourneyRegistry<string>, id };
    logMark(this.config, { id, mark: undefined, sinceStart: 0, marks: state.marks });
  }

  mark(id: string, mark: Mark, meta?: Record<string, unknown>): void {
    const state = this.active.get(id);
    if (!state || state.marks[mark] != null) return;

    const at = performance.now();
    state.marks[mark] = at;
    if (meta) state.meta[mark] = meta;

    const isTerminal = this.config.terminalMark != null && mark === this.config.terminalMark;

    let deltas: JourneyDeltas | undefined;
    let bottleneck: string | null | undefined;
    let assetLoadTime: AssetSummary | undefined;
    let callSummary: CallSummaryEntry[] | undefined;

    if (isTerminal) {
      deltas = this.computeDeltasFor(state);
      bottleneck = this.computeBottleneck(deltas);
      // Always from document time origin (0), not `state.startedAt` — on an SPA nav, startedAt is
      // the click time, well after chunks that were already idle-prefetched during the prior page
      // finished loading. Filtering by `state.startedAt` excludes every one of them and reports
      // every bucket as empty; see the regression this exact bug caused, below.
      if (this.assetLoadTimeEnabled) assetLoadTime = collectAssetSummary(0, at);
      if (this.timedCallsEnabled) callSummary = summarizeCalls(state.calls ?? []);
    }

    const ctx = {
      id,
      mark,
      at,
      sinceStart: Math.round(at - state.startedAt),
      mode: state.mode,
      meta,
      marks: { ...state.marks },
      startedAt: state.startedAt,
      deltas,
      bottleneck,
      assetLoadTime
    };

    logMark(this.config, ctx);
    emitAll(this.config.emit?.[mark]?.(ctx));

    if (isTerminal) {
      if (callSummary) this.fanOutCallSummary(id, callSummary);
      logTerminalReport(this.config, state, ctx, callSummary);
      // Nothing reads `calls` past this point — drop it so a long-lived session (e.g. SQL IDE,
      // whose socket keeps calling recordCall long after page-load) doesn't grow it forever.
      state.isDone = true;
      state.calls = undefined;
      if (current?.registry === (this as unknown as JourneyRegistry<string>) && current.id === id) {
        current = null;
      }
    }
  }

  private fanOutCallSummary(id: string, summary: CallSummaryEntry[]): void {
    for (const entry of summary) {
      // serverMs (WS, real backend value) and networkMs (GraphQL, browser-derived) are mutually
      // exclusive by kind — spread only the one that applies, so the other never appears as an
      // explicit (if empty) key on the wrong kind's payload.
      const serverFields =
        entry.totalServerMs != null
          ? { totalServerMs: Math.round(entry.totalServerMs), maxServerMs: entry.maxServerMs }
          : {};
      const networkFields =
        entry.totalNetworkMs != null
          ? { totalNetworkMs: Math.round(entry.totalNetworkMs), maxNetworkMs: entry.maxNetworkMs }
          : {};
      const resourceDurationFields =
        entry.totalResourceDurationMs != null
          ? {
              totalResourceDurationMs: Math.round(entry.totalResourceDurationMs),
              maxResourceDurationMs: entry.maxResourceDurationMs
            }
          : {};

      metrics.track({
        eventName: EventName.ui.page.callSummary,
        eventType: EventType.system,
        payload: {
          journey: this.config.name,
          id,
          kind: entry.kind,
          name: entry.name,
          count: entry.count,
          totalMs: Math.round(entry.totalMs),
          maxMs: entry.maxMs,
          earliestStartedAt: Math.round(entry.earliestStartedAt),
          latestEndedAt: Math.round(entry.latestEndedAt),
          ...serverFields,
          ...networkFields,
          ...resourceDurationFields
        }
      });
    }
  }

  recordCall(id: string, name: string, durationMs: number, opts: RecordCallOpts): void {
    if (!this.timedCallsEnabled) return;
    const state = this.active.get(id);
    if (!state || state.isDone) return;
    state.calls ??= [];
    // `isDone` only guards a journey that already finished; one that stalls before its terminal
    // mark (a failed load, a tab left open mid-error) would otherwise grow this forever.
    if (state.calls.length >= MAX_TIMED_CALLS_PER_JOURNEY) return;
    state.calls.push({
      name,
      kind: opts.kind,
      durationMs: Math.round(durationMs),
      startedAt: opts.startedAt,
      endedAt: opts.endedAt,
      // Keep whichever keys opts actually carried off the object entirely rather than storing them
      // as an explicit `undefined` — a console dump of `state.calls` (or anything else that inspects
      // a raw TimedCall) shouldn't show e.g. `serverMs: undefined` on a GraphQL call.
      ...(opts.serverMs != null ? { serverMs: opts.serverMs } : {}),
      ...(opts.networkMs != null ? { networkMs: opts.networkMs } : {}),
      ...(opts.resourceDurationMs != null ? { resourceDurationMs: opts.resourceDurationMs } : {})
    });
  }

  computeDeltas(id: string): JourneyDeltas | null {
    const state = this.active.get(id);
    if (!state) return null;
    return this.computeDeltasFor(state);
  }

  getSnapshot(
    id: string
  ):
    | { startedAt: number; mode: 'reload' | 'spa'; marks: Partial<Record<Mark, number>>; calls: TimedCall[] }
    | undefined {
    const state = this.active.get(id);
    if (!state) return undefined;
    return { startedAt: state.startedAt, mode: state.mode, marks: { ...state.marks }, calls: [...(state.calls ?? [])] };
  }

  reset(id: string): void {
    this.active.delete(id);
    this.pending.delete(id);
    if (current?.registry === (this as unknown as JourneyRegistry<string>) && current.id === id) {
      current = null;
    }
  }

  private computeDeltasFor(state: JourneyState<Mark>): JourneyDeltas {
    const result: JourneyDeltas = {};
    for (const [name, spec] of Object.entries(this.config.deltas ?? {})) {
      const fromAt =
        spec.from === 'start'
          ? state.startedAt
          : typeof spec.from === 'function'
            ? spec.from(state.marks)
            : state.marks[spec.from as Mark];
      const toAt = state.marks[spec.to];
      result[name] = fromAt != null && toAt != null ? Math.round(toAt - fromAt) : null;
    }
    return result;
  }

  private computeBottleneck(deltas: JourneyDeltas): string | null {
    let best: string | null = null;
    let bestMs = -Infinity;
    for (const name of this.config.bottleneckCandidates ?? []) {
      const ms = deltas[name];
      if (ms != null && ms > bestMs) {
        bestMs = ms;
        best = name;
      }
    }
    return best;
  }
}
